import {
    Box, Toolbar, TextField, InputAdornment, Divider, List,
    ListItem, ListItemButton, ListItemAvatar, Badge, Avatar,
    ListItemText, Typography, Fab,
    styled,
    CircularProgress,
    Chip
} from '@mui/material';
import { AddComment, Groups, Search, VerifiedUser } from '@mui/icons-material';
import MentorBadge from '../../components/MentorBadge';
import InfiniteScroll from 'react-infinite-scroll-component';


const StyledBadge = styled(Badge)(({ theme }) => ({
    "& > .MuiBadge-badge": {   // 👈 only target THIS badge
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.main,
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        "&::after": {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            animation: "ripple 1.2s infinite ease-in-out",
            border: "1px solid currentColor",
            content: '""',
        },
    },
    "@keyframes ripple": {
        "0%": {
            transform: "scale(.8)",
            opacity: 1,
        },
        "100%": {
            transform: "scale(2.4)",
            opacity: 0,
        },
    },
}));


const RecentChatList = ({
    chats,
    hasMore,
    loadMore,
    selectedId,
    onSelect,
    onSwitchToContacts,
    getImageUrlWithToken,
    searchTerm,
    setSearchTerm

}) => {

    console.log("Rendering RecentChatList with chats:", chats);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Search Header */}
            <Toolbar sx={{ px: 2, py: 1, bgcolor: 'background.paper', display: 'flex', gap: 1 }} disableGutters>
                <TextField
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    placeholder="Search chats..."
                    InputProps={{
                        startAdornment: <InputAdornment position="start">
                            <Search fontSize="small" />
                        </InputAdornment>,
                        sx: { borderRadius: 5 }
                    }}
                />
            </Toolbar>
            <Divider />

            {/* List of Conversations */}
            <List sx={{ flexGrow: 1, overflowY: 'auto' }} id="scrollableDiv">
                <InfiniteScroll
                    dataLength={chats.length}
                    next={loadMore}
                    hasMore={hasMore}
                    loader={<Box sx={{ textAlign: "center", py: 1 }}><CircularProgress size={20} /></Box>}
                    scrollableTarget="scrollableDiv"
                    endMessage={
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 1 }}>
                            No more chats
                        </Typography>
                    }
                >
                    {chats.map((c) => {
                        const isGroup = c.is_group === 1;
                        const isOnline = c.is_online === 1;
                        const lastMessage = c.last_message?.content || '';
                        const time = c.last_message?.created_at
                            ? new Date(c.last_message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : '';

                        return (
                            <ListItem key={c.chat_id} disablePadding>
                                <ListItemButton
                                    selected={selectedId === c.chat_id}
                                    onClick={() => onSelect(c)}
                                    sx={{ borderLeft: selectedId === c.chat_id ? '4px solid #e90000' : '4px solid transparent' }}
                                >
                                    <ListItemAvatar>
                                        <StyledBadge
                                            color="success"
                                            variant="dot"
                                            invisible={!isOnline || isGroup}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            overlap="circular"
                                        >
                                            {c.is_mentor ? (
                                                <MentorBadge isSmall>
                                                    <Avatar src={getImageUrlWithToken(c.chat_image)} />
                                                </MentorBadge>
                                            ) : (
                                                <Avatar alt={c.chat_name} src={getImageUrlWithToken(c.chat_image)}>
                                                    {isGroup && <Groups />}
                                                </Avatar>
                                            )}

                                        </StyledBadge>
                                    </ListItemAvatar>

                                    <ListItemText
                                        primary={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                {c.chat_name}
                                                {c.is_verified && <VerifiedUser fontSize="small" color="success" sx={{ fontSize: 16, mb: 0.3 }} />}
                                            </span>
                                        }
                                        secondary={lastMessage}
                                        slotProps={{
                                            primary: {
                                                noWrap: true,
                                                fontSize: 14,
                                                fontWeight: 500,
                                            },
                                            secondary: {
                                                noWrap: true,
                                                fontSize: 12,
                                            }
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1, gap: 0.5 }}>
                                        {c.unread_messages > 0 && (
                                            <Chip
                                                size="small"
                                                label={c.unread_messages}
                                                color="primary"
                                                onClick={() => { }}
                                                sx={{
                                                    height: 20,
                                                    fontSize: 12,
                                                    fontWeight: 'bold',
                                                    '& .MuiChip-label': { px: "0.4rem" }
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                            {time}
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </InfiniteScroll>
            </List>

            {/* FAB to Open Connection List */}
            <Fab
                color="primary"
                aria-label="add"
                onClick={onSwitchToContacts}
                sx={{ position: 'absolute', bottom: 24, right: 24 }}
            >
                <AddComment />
            </Fab>
        </Box>
    );
};

export default RecentChatList;